// Horse Detail Page
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { horseAPI, saleOpportunityAPI, saleAPI, activityAPI } from '../../services/api';
import api from '../../services/api';
import SidebarLayout from '../../components/layout/SidebarLayout';
import HorseOverview from '../../components/horses/HorseOverview';
import RacingTab from './RacingTab';
import BreedingTab from './BreedingTab';
import OwnershipTab from './OwnershipTab';
import HorseshoeIcon from '../../components/icons/HorseshoeIcon';
import FarmFolioTab from '../../components/farmfolio/FarmFolioTab';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import OfflineConflictBadge from '../../components/common/OfflineConflictBadge';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import FileUpload from '../../components/common/FileUpload';
import InvoicePdfPreviewDialog from '../../components/shared/InvoicePdfPreviewDialog';
import { Input } from '../../components/ui/input';
import { Edit, Camera, Upload, ChevronDown, Gavel, ChevronRight, FileText, Loader2, Search, Archive, ArchiveRestore, RotateCw, Plus } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { calcInvoiceAmount, calcGrossBilled } from '../../utils/invoiceCalc';
import CameraCaptureDialog from '../../components/shared/CameraCaptureDialog';
import CreateSaleOpportunityDialog from '../../components/horses/CreateSaleOpportunityDialog';
import { useAuth } from '../../context/AuthContext';

const stageBadges = {
  presale_create: { label: 'Potential Sale Entry', className: 'bg-gray-100 text-gray-700' },
  presale_confirm: { label: 'Confirmed Sale Entry', className: 'bg-blue-100 text-blue-700' },
  presale_deliverables: { label: 'Tracking Deliverables', className: 'bg-yellow-100 text-yellow-700' },
  presale_ready: { label: 'Ready for Sale', className: 'bg-green-100 text-green-700' },
  live_sale: { label: 'Live Sale', className: 'bg-primary text-white' },
  post_sale: { label: 'Complete', className: 'bg-muted text-muted-foreground' },
};

// Maps stored color enum values to the human-readable labels shown in dropdowns.
// Matches the SelectItem labels in HorseOverview.js and HorseFormPage.js.
const COLOR_LABELS = {
  bay:            'Bay',
  black:          'Black',
  chestnut:       'Chestnut',
  dark_bay_brown: 'Dark Bay/Brown',
  gray_roan:      'Gray/Roan',
  palomino:       'Palomino',
  white:          'White',
};

const resultBadges = {
  sold: { label: 'SOLD', className: 'bg-green-600 text-white' },
  rna: { label: 'RNA', className: 'bg-yellow-600 text-white' },
  withdrawn: { label: 'Withdrawn', className: 'bg-red-100 text-red-700' },
};

// Helper function to calculate horse age category
const calculateAge = (horse) => {
  if (!horse.birthdate) return '-';
  
  const currentYear = new Date().getFullYear();
  const birthYear = new Date(horse.birthdate).getFullYear();
  const age = currentYear - birthYear;
  
  // Current year foal logic
  if (age === 0) {
    if (horse.still_nursing) return 'Suckling';
    if (horse.separated_from_dam) return 'Weanling';
    return 'Foal';
  }
  
  // 1 year old
  if (age === 1) return 'Yearling';
  
  // 2+ years old
  return `${age}YO`;
};

// Helper function to calculate gender category
const calculateGender = (horse) => {
  if (!horse.birthdate || !horse.sex) return '-';
  
  const currentYear = new Date().getFullYear();
  const birthYear = new Date(horse.birthdate).getFullYear();
  const age = currentYear - birthYear;
  
  if (horse.sex === 'female') {
    return age < 5 ? 'Filly' : 'Mare';
  } else {
    // Male logic
    if (age >= 5) return 'Horse';
    if (horse.castrated) return 'Gelding';
    if (horse.cryptorchid) return 'Ridgeling';
    return 'Colt';
  }
};

const HorseDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user: currentUser, isViewOnly } = useAuth();
  const viewOnly = isViewOnly();
  const isTenantAdmin = currentUser?.roles?.includes('tenant_system_admin') || currentUser?.roles?.includes('system_owner');
  const isFarmMgr = currentUser?.roles?.includes('tenant_farm_manager');
  const qc = useQueryClient();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: QK.horse(id),
    queryFn: async () => {
      const response = await horseAPI.get(id);
      const horse = response.data;

      // Female-specific: cover record + pregnancy (parallel, non-blocking)
      let currentFertilityStatus = null;
      let pregnancyInfo = null;
      if (horse.sex === 'female') {
        const [fertRes, pregRes] = await Promise.allSettled([
          api.get(`/cover-records/current/${id}`),
          api.get(`/breeding-seasons/pregnancy-info/${id}`),
        ]);
        currentFertilityStatus = fertRes.status === 'fulfilled' ? (fertRes.value?.data?.fertility_status || null) : null;
        pregnancyInfo = pregRes.status === 'fulfilled' ? (pregRes.value?.data || null) : null;
      }

      // Sale opportunities (all, filtered to this horse) — parallelise sale lookups
      const oppsRes = await saleOpportunityAPI.list();
      const horseOpps = (oppsRes.data || []).filter(opp => opp.horse_id === id);
      horseOpps.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      const uniqueSaleIds = [...new Set(horseOpps.map(o => o.sale_id).filter(Boolean))];
      const saleResults = await Promise.allSettled(
        uniqueSaleIds.map(sid => saleAPI.get(sid).then(r => [sid, r.data]))
      );
      const salesMap = Object.fromEntries(
        saleResults.filter(r => r.status === 'fulfilled').map(r => r.value)
      );

      // Invoices for this horse
      let horseInvoices = [], allHorseInvoices = [];
      try {
        const invRes = await api.get('/invoices');
        const approvedStatuses = ['approved', 'delivered', 'delivered_manually', 'delivered_via_greendoor'];
        allHorseInvoices = (invRes.data || []).filter(i => i.horse_id === id);
        horseInvoices = allHorseInvoices.filter(i => approvedStatuses.includes(i.invoice_status));
      } catch { /* ignore */ }

      // Secondary: billing entities + tenant metadata + Overview pre-warm data (all non-blocking)
      let billingEntities = [], tenantName = '', isFarm = false;
      let overviewTasks = [], overviewComms = [], overviewNotes = [];
      try {
        const [beRes, tenantRes, tasksRes, commsRes, notesRes] = await Promise.allSettled([
          api.get('/billing-entities'),
          currentUser?.tenant_id ? api.get(`/tenants/${currentUser.tenant_id}`) : Promise.resolve(null),
          activityAPI.list({ activity_type: 'task', horse_id: id }),
          activityAPI.list({ activity_type: 'communication', horse_id: id }),
          api.get(`/conformation-notes?horse_id=${id}`),
        ]);
        billingEntities = beRes.status === 'fulfilled' ? (beRes.value?.data || []) : [];
        if (tenantRes.status === 'fulfilled' && tenantRes.value?.data) {
          tenantName = tenantRes.value.data?.name || '';
          isFarm = tenantRes.value.data?.is_farm === true;
        }
        overviewTasks = tasksRes.status  === 'fulfilled' ? (tasksRes.value?.data  || []) : [];
        overviewComms = commsRes.status  === 'fulfilled' ? (commsRes.value?.data  || []) : [];
        overviewNotes = notesRes.status  === 'fulfilled' ? (notesRes.value?.data  || []) : [];
      } catch { /* ignore */ }

      return { horse, saleOpportunities: horseOpps, sales: salesMap, horseInvoices,
               allHorseInvoices, billingEntities, tenantName, isFarm,
               currentFertilityStatus, pregnancyInfo,
               overviewTasks, overviewComms, overviewNotes };
    },
    enabled: !!id,
    onError: () => { toast.error('Failed to load horse details'); navigate('/horses'); },
  });

  // FarmFolio metrics — separate query gated on isFarm
  const isFarm = data?.isFarm ?? false;
  const { data: horseMetrics } = useQuery({
    queryKey: QK.farmfolioMetricsHorse(id),
    queryFn: () => api.get(`/farmfolio/metrics/horse/${id}`).then(r => r.data),
    enabled: isFarm && !!id,
  });

  // Destructure server state with defaults
  const horse               = data?.horse ?? null;
  const saleOpportunities   = data?.saleOpportunities ?? [];
  const sales               = data?.sales ?? {};
  const horseInvoices       = data?.horseInvoices ?? [];
  const allHorseInvoices    = data?.allHorseInvoices ?? [];
  const billingEntities     = data?.billingEntities ?? [];
  const tenantName          = data?.tenantName ?? '';
  const currentFertilityStatus = data?.currentFertilityStatus ?? null;
  const pregnancyInfo       = data?.pregnancyInfo ?? null;

  const invalidateHorse = () => qc.invalidateQueries({ queryKey: QK.horse(id) });

  // Pre-warm HorseOverview cache — useLayoutEffect fires before paint so cache
  // is populated before HorseOverview's useQuery calls execute.
  useLayoutEffect(() => {
    if (!data?.horse?.id) return;
    const hid = data.horse.id;
    if (data.overviewTasks) qc.setQueryData(QK.activities({ activity_type: 'task',          horse_id: hid }), data.overviewTasks);
    if (data.overviewComms) qc.setQueryData(QK.activities({ activity_type: 'communication', horse_id: hid }), data.overviewComms);
    if (data.overviewNotes) qc.setQueryData(QK.conformationNotes(hid), data.overviewNotes);
  }, [data?.horse?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── UI state ──────────────────────────────────────────────────────────────
  const [archiving, setArchiving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [profilePhotoDataUrl, setProfilePhotoDataUrl] = useState(null);
  const [photoRotation, setPhotoRotation] = useState(0);
  const [savingRotation, setSavingRotation] = useState(false);
  const [showCreateOppDialog, setShowCreateOppDialog] = useState(false);
  const [createOppSavesStatus, setCreateOppSavesStatus] = useState(false);
  const [oppsSearch, setOppsSearch] = useState('');
  const [savingBillingEntity, setSavingBillingEntity] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [horseInvoiceSearch, setHorseInvoiceSearch] = useState('');
  const [horseMetricsView, setHorseMetricsView] = useState('ytd');

  const handleRotateAndSave = async () => {
    // Use post-upload preview if available, otherwise load from the stored URL
    const photoSrc = profilePhotoDataUrl ||
      (horse?.main_photo_url
        ? (horse.main_photo_url.startsWith('http') ? horse.main_photo_url : `${window.location.origin}${horse.main_photo_url}`)
        : null);
    if (!photoSrc) return;
    setSavingRotation(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = photoSrc; });
      const canvas = document.createElement('canvas');
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((90 * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      const formData = new FormData();
      formData.append('file', blob, 'rotated-photo.jpg');
      formData.append('horse_id', id);
      formData.append('description', 'Profile Photo');
      formData.append('category', 'photo');

      const token = localStorage.getItem('token');
      const response = await fetch(`${window.location.origin}/api/files/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      const fileData = await response.json();

      await horseAPI.update(id, { main_photo_url: `/api/files/public/${fileData.id}` });
      setProfilePhotoDataUrl(URL.createObjectURL(blob));
      setPhotoRotation(0);
      toast.success('Photo rotated and saved');
    } catch (e) {
      console.error('Failed to rotate photo:', e);
      toast.error('Failed to save rotated photo');
    } finally {
      setSavingRotation(false);
    }
  };

  useEffect(() => {
    setActiveTab(searchParams.get('tab') || (isFarmMgr ? 'farmfolio' : 'overview'));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('horse_id', id);
      formData.append('description', 'Profile Photo');
      formData.append('category', 'photo');

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/files/upload`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const fileData = await response.json();
      
      // Update horse with new profile photo URL (store relative path)
      await horseAPI.update(id, { 
        main_photo_url: `/api/files/public/${fileData.id}`
      });
      
      // Immediately update the preview with the uploaded file
      const previewUrl = `${window.location.origin}/api/files/public/${fileData.id}`;
      const previewRes = await fetch(previewUrl);
      if (previewRes.ok) {
        const blob = await previewRes.blob();
        setProfilePhotoDataUrl(URL.createObjectURL(blob));
        setPhotoRotation(0);
      }
      
      await invalidateHorse();
      toast.success('Profile photo updated');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCameraCapture = async (file) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('horse_id', id);
      formData.append('description', 'Profile Photo');
      formData.append('category', 'photo');

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/files/upload`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Upload failed');

      const fileData = await response.json();
      await horseAPI.update(id, {
        main_photo_url: `/api/files/public/${fileData.id}`
      });

      // Immediately update the preview
      const previewUrl = `${window.location.origin}/api/files/public/${fileData.id}`;
      const previewRes = await fetch(previewUrl);
      if (previewRes.ok) {
        const blob = await previewRes.blob();
        setProfilePhotoDataUrl(URL.createObjectURL(blob));
        setPhotoRotation(0);
      }

      toast.success('Profile photo updated');
      await invalidateHorse();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaleStatusChange = async (newStatus) => {
    if (newStatus === 'Sale Opportunity') {
      // Open dialog first — status only saves after opportunity is created
      setCreateOppSavesStatus(true);
      setShowCreateOppDialog(true);
      return;
    }
    try {
      await horseAPI.update(horse.id, { sale_status: newStatus });
      qc.setQueryData(QK.horse(id), prev => prev ? { ...prev, horse: { ...prev.horse, sale_status: newStatus } } : prev);
      toast.success('Sale status updated');
    } catch (error) {
      console.error('Error updating sale status:', error);
      toast.error('Failed to update sale status');
    }
  };

  const handleKeepInCare = async () => {
    try {
      await horseAPI.update(horse.id, { keep_in_care: true });
      qc.setQueryData(QK.horse(id), prev => prev ? { ...prev, horse: { ...prev.horse, keep_in_care: true } } : prev);
      toast.success('Horse marked as keep in care — automated care will resume');
    } catch {
      toast.error('Failed to update keep in care');
    }
  };

  const handleCreateOppSuccess = async () => {
    try {
      await horseAPI.update(horse.id, { sale_status: 'Sale Opportunity' });
      qc.setQueryData(QK.horse(id), prev => prev ? { ...prev, horse: { ...prev.horse, sale_status: 'Sale Opportunity' } } : prev);
      toast.success('Sale status updated');
    } catch (error) {
      console.error('Error saving sale status:', error);
      toast.error('Failed to update sale status');
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      const newArchivedState = !horse.is_archived;
      await horseAPI.update(horse.id, { is_archived: newArchivedState, archived_at: newArchivedState ? new Date().toISOString() : null });
      qc.setQueryData(QK.horse(id), prev => prev ? { ...prev, horse: { ...prev.horse, is_archived: newArchivedState } } : prev);
      toast.success(newArchivedState ? 'Horse archived' : 'Horse restored');
    } catch (error) {
      console.error('Failed to archive/restore:', error);
      toast.error('Failed to update archive status');
    } finally {
      setArchiving(false);
    }
  };

  if (isLoading) {
    return (
      <SidebarLayout>
        <div className="space-y-4 animate-pulse">
          {/* Header skeleton */}
          <div className="flex items-center gap-4 pb-4 border-b">
            <Skeleton className="h-24 w-24 rounded-full shrink-0" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-7 w-48" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
              <div className="grid grid-cols-4 gap-4 mt-2">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-4 w-full" />)}
              </div>
            </div>
            <Skeleton className="h-9 w-40 rounded" />
          </div>
          {/* Tab bar skeleton */}
          <div className="flex gap-1 border-b pb-0">
            {[72, 64, 56, 80, 64, 72, 80, 56].map((w, i) => (
              <Skeleton key={i} className="h-9 w-16 rounded-t" />
            ))}
          </div>
          {/* Two-column overview content skeleton */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <Skeleton className="h-44 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-52 w-full rounded-lg" />
              <Skeleton className="h-36 w-full rounded-lg" />
              <Skeleton className="h-40 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!horse) {
    return (
      <SidebarLayout>
        <div className="p-8">
          <p className="text-muted-foreground">Horse not found</p>
        </div>
      </SidebarLayout>
    );
  }

  const displayName = horse.system_name || horse.name || 'Unnamed Horse';

  // --- Top Line Summary computation ---
  const horseYr = new Date().getFullYear();
  const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v ?? 0);
  const sumHorseAmt = (list) => list.reduce((s, i) => s + calcGrossBilled(i), 0);
  const ytdAllHorseInvs = allHorseInvoices.filter(i => i.invoice_date && new Date(i.invoice_date).getFullYear() === horseYr);
  const horseTotalAmtYTD     = sumHorseAmt(ytdAllHorseInvs);
  const horseTotalAmtAll     = sumHorseAmt(allHorseInvoices);
  const horseDraftYTD        = sumHorseAmt(ytdAllHorseInvs.filter(i => i.invoice_status === 'draft'));
  const horseApprovedYTD     = sumHorseAmt(ytdAllHorseInvs.filter(i => i.invoice_status === 'approved'));
  const horseDeliveredYTD    = sumHorseAmt(ytdAllHorseInvs.filter(i => ['delivered', 'delivered_manually', 'delivered_via_greendoor'].includes(i.invoice_status)));
  const horsePaidYTD         = sumHorseAmt(ytdAllHorseInvs.filter(i => i.payment_status === 'paid'));
  const horseUnpaidYTD       = sumHorseAmt(ytdAllHorseInvs.filter(i => i.payment_status === 'unpaid' && ['delivered', 'delivered_manually', 'delivered_via_greendoor'].includes(i.invoice_status)));
  // Sales summary from approved/delivered invoices
  let horseCmsnYTD = 0, horseCmsnAll = 0, horseProcYTD = 0, horseProcAll = 0;
  horseInvoices.forEach(inv => {
    const invYear = inv.invoice_date ? new Date(inv.invoice_date).getFullYear() : null;
    const commission = parseFloat(inv.commission_amount || 0);
    if (commission > 0) { horseCmsnAll += commission; if (invYear === horseYr) horseCmsnYTD += commission; }
    if (inv.add_sale_proceeds) {
      const net = (parseFloat(inv.sale_net_proceeds || 0) - commission) * (parseFloat(inv.owner_percent || 100) / 100);
      horseProcAll += net;
      if (invYear === horseYr) horseProcYTD += net;
    }
  });
  let horseGrossAll = 0, horseGrossYTD = 0;
  saleOpportunities.filter(o => o.result === 'sold' && o.sale_price > 0).forEach(o => {
    const sp = parseFloat(o.sale_price || 0);
    horseGrossAll += sp;
    const d = (o.date_entered || o.session_date || o.updated_at || '');
    const yr = d ? new Date(d).getFullYear() : null;
    if (!yr || yr === horseYr) horseGrossYTD += sp;
  });
  const horseHasSales = horseCmsnAll > 0 || horseGrossAll > 0;

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <Card className="bg-white border-primary shadow-sm">
          <CardContent className="p-3 sm:p-6">

            {/* Dropdown + Avatar + Name — one row on desktop, stacked on mobile */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
              <div className="flex justify-end sm:order-last sm:ml-auto shrink-0 mb-1 sm:mb-0">
                {!viewOnly && !isFarmMgr && (
                  <div className="flex items-center gap-1">
                    {horse.sale_status === 'Sale Opportunity' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 w-7 p-0 border-primary text-primary"
                        title="Add another sale opportunity"
                        onClick={() => { setCreateOppSavesStatus(false); setShowCreateOppDialog(true); }}
                        data-testid="add-opp-plus-btn"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="text-xs uppercase tracking-wide border-primary text-primary gap-1">
                          {horse.sale_status || 'Not for Sale'}
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleSaleStatusChange('Sale Prospect')}>Sale Prospect</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleSaleStatusChange('Sale Opportunity')}>Sale Opportunity</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleSaleStatusChange('Not for Sale')}>Not for Sale</DropdownMenuItem>
                        {isTenantAdmin && (
                          <>
                            <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleArchive} disabled={archiving} className={horse.is_archived ? 'text-green-700' : 'text-orange-600'}>
                            {horse.is_archived ? <><ArchiveRestore className="w-4 h-4 mr-2" /> Restore Horse</> : <><Archive className="w-4 h-4 mr-2" /> Archive Horse</>}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {/* Keep in Care button — shown when horse is sold without keep_in_care (FarmFolio tenants) */}
                  {horse.sale_status === 'Sold' && !horse.keep_in_care && isFarm && !viewOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs border-green-600 text-green-700 hover:bg-green-50"
                      onClick={handleKeepInCare}
                      data-testid="keep-in-care-btn"
                    >
                      Keep in Care
                    </Button>
                  )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-10 h-10 sm:w-24 sm:h-24 bg-muted rounded-full flex items-center justify-center border-2 border-border shrink-0 relative group cursor-pointer"
                  onClick={() => setProfilePhotoPreview(true)}
                >
                  {(profilePhotoDataUrl || horse?.main_photo_url) ? (
                    <img
                      src={profilePhotoDataUrl || (horse.main_photo_url.startsWith('http') ? horse.main_photo_url : `${window.location.origin}${horse.main_photo_url}`)}
                      alt={displayName} className="w-full h-full rounded-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <HorseshoeIcon className="w-5 h-5 sm:w-10 sm:h-10 text-muted-foreground" />
                  )}
                  <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="w-4 h-4 text-white" />
                  </div>
                </div>
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <h1 className="font-serif text-xl sm:text-3xl font-bold text-foreground leading-tight">{displayName}</h1>
                  <OfflineConflictBadge record={horse} entityType="horses" onDismissed={invalidateHorse} />
                  {horse.is_archived && <Badge variant="outline" className="border-orange-400 text-orange-600 font-semibold text-xs shrink-0">Archived</Badge>}
                  {horse.graduate && <Badge variant="default" className="bg-blue-600 text-xs shrink-0">Graduate</Badge>}
                  {horse.is_top_horse && <Badge variant="default" className="bg-yellow-600 text-xs shrink-0">Top Horse</Badge>}
                  {horse.on_layup && <Badge className="bg-red-600 text-white text-xs shrink-0" data-testid="horse-layup-badge">LAYUP</Badge>}
                  {pregnancyInfo?.is_pregnant && (() => {
                    const since = new Date((pregnancyInfo.pregnant_since || '') + 'T00:00:00');
                    const endDate = pregnancyInfo.sold && pregnancyInfo.sold_date
                      ? new Date(pregnancyInfo.sold_date + 'T00:00:00')
                      : (() => { const n = new Date(); n.setHours(0,0,0,0); return n; })();
                    const daysSince = pregnancyInfo.pregnant_since ? Math.floor((endDate - since) / 86400000) + 1 : null;
                    const dueStr = !pregnancyInfo.sold && pregnancyInfo.estimated_due_date
                      ? new Date(pregnancyInfo.estimated_due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : null;
                    const soldStr = pregnancyInfo.sold && pregnancyInfo.sold_date
                      ? new Date(pregnancyInfo.sold_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : null;
                    return (
                      <Badge className="bg-pink-100 text-pink-800 border border-pink-300 text-xs shrink-0 font-semibold" data-testid="horse-pregnant-badge">
                        Pregnant{daysSince !== null ? ` · ${daysSince}d` : ''}{dueStr ? ` · Due ${dueStr}` : ''}{soldStr ? ` · Sold ${soldStr}` : ''}
                      </Badge>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Row 3: Details */}
            <div
              className="gap-x-4 gap-y-1 text-sm"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
            >
              <div>
                <p className="text-muted-foreground text-xs uppercase">Color</p>
                <p className="font-medium text-xs">{COLOR_LABELS[horse.color] || horse.color?.replace(/_/g, ' ') || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase">Gender</p>
                <p className="font-medium capitalize text-xs">{calculateGender(horse)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase">Age</p>
                <p className="font-medium text-xs">{calculateAge(horse)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase">Type</p>
                <p className="font-medium capitalize text-xs">
                  {horse.sex === 'male' ? (horse.horse_type_male?.replace(/_/g, ' ') || '-') : (horse.horse_type_female?.replace(/_/g, ' ') || '-')}
                </p>
              </div>
              {horse.sex === 'female' && (
                <div>
                  <p className="text-muted-foreground text-xs uppercase">Fertility Status</p>
                  <p className="font-medium capitalize text-xs">{currentFertilityStatus || horse.fertility_status || '-'}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground text-xs uppercase">Foaled</p>
                <p className="font-medium text-xs">{horse.birthdate ? new Date(horse.birthdate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent gap-6">
            {['Overview', 'Ownership', 'Racing', 'Photos & Files', 'Financials', 'Sales Opportunities', 'FarmFolio', 'Breeding'].filter(tab => {
              if (tab === 'Breeding' && horse.castrated) return false;
              if (tab === 'FarmFolio' && !isFarm) return false;
              // Farm Managers only see FarmFolio and Breeding
              if (isFarmMgr && !['Overview', 'FarmFolio', 'Breeding', 'Sales Opportunities'].includes(tab)) return false;
              return true;
            }).map(tab => (
              <TabsTrigger 
                key={tab} 
                value={tab.toLowerCase().replace(/\s+/g, '-')}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-2"
              >
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <HorseOverview horse={horse} onUpdate={invalidateHorse} canEditAccount={!viewOnly} isFarm={isFarm} />
          </TabsContent>

          <TabsContent value="ownership">
            <OwnershipTab horse={horse} />
          </TabsContent>

          <TabsContent value="breeding">
            <BreedingTab horse={horse} onPregnancyChanged={invalidateHorse} />
          </TabsContent>

          <TabsContent value="racing">
            <RacingTab horse={horse} />
          </TabsContent>

          <TabsContent value="photos-&-files">
            <FileUpload 
              tenantId={horse.tenant_id} 
              entityType="horse" 
              entityId={horse.id}
              splitView={true}
              currentProfilePhotoUrl={horse.main_photo_url || ''}
              onProfilePhotoSet={(newUrl) => qc.setQueryData(QK.horse(id), prev => prev ? { ...prev, horse: { ...prev.horse, main_photo_url: newUrl } } : prev)}
            />
          </TabsContent>

          <TabsContent value="financials">
            {/* Unified Top Line Summary — 3 boxes + collapsible Sales + FarmFolio */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base">Top Line Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 3 main boxes — always shown */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">Total Billed Amount</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtCurrency(horseTotalAmtYTD)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtCurrency(horseTotalAmtAll)}</span></div>
                    </div>
                  </div>
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">YTD by Status</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">Draft:</span><span className="text-xs">{fmtCurrency(horseDraftYTD)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">Approved:</span><span className="text-xs">{fmtCurrency(horseApprovedYTD)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">Sent Out:</span><span className="text-xs">{fmtCurrency(horseDeliveredYTD)}</span></div>
                    </div>
                  </div>
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">YTD Payment Status</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">Paid:</span><span className="text-xs text-primary">{fmtCurrency(horsePaidYTD)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">Unpaid:</span><span className="text-xs text-red-600">{fmtCurrency(horseUnpaidYTD)}</span></div>
                    </div>
                  </div>
                </div>

                {/* Sales Summary — shown if this horse has sales activity */}
                {horseHasSales && (
                  <Collapsible defaultOpen>
                    <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase hover:text-foreground transition-colors w-full text-left">
                      <ChevronDown className="w-3.5 h-3.5" />
                      Sales Summary
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                        <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                          <p className="text-xs text-muted-foreground uppercase">All Commissions</p>
                          <div className="space-y-1">
                            <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtCurrency(horseCmsnYTD)}</span></div>
                            <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtCurrency(horseCmsnAll)}</span></div>
                          </div>
                        </div>
                        <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                          <p className="text-xs text-muted-foreground uppercase">Gross Sales</p>
                          <div className="space-y-1">
                            <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtCurrency(horseGrossYTD)}</span></div>
                            <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtCurrency(horseGrossAll)}</span></div>
                          </div>
                        </div>
                        <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                          <p className="text-xs text-muted-foreground uppercase">Owner Net Proceeds</p>
                          <div className="space-y-1">
                            <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtCurrency(horseProcYTD)}</span></div>
                            <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtCurrency(horseProcAll)}</span></div>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* FarmFolio Summary — shown for farm tenants */}
                {isFarm && horseMetrics && (
                  <Collapsible defaultOpen>
                    <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase hover:text-foreground transition-colors w-full text-left">
                      <ChevronDown className="w-3.5 h-3.5" />
                      FarmFolio Summary
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                        <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                          <p className="text-xs text-muted-foreground uppercase">Total Cost of Care</p>
                          <div className="space-y-1">
                            <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtCurrency(horseMetrics.total_cost_of_care?.ytd ?? 0)}</span></div>
                            <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtCurrency(horseMetrics.total_cost_of_care?.all_time ?? 0)}</span></div>
                          </div>
                        </div>
                        <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                          <p className="text-xs text-muted-foreground uppercase">Net Revenue</p>
                          <div className="space-y-1">
                            <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtCurrency(horseMetrics.net_revenue?.ytd ?? 0)}</span></div>
                            <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtCurrency(horseMetrics.net_revenue?.all_time ?? 0)}</span></div>
                          </div>
                        </div>
                        <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                          <p className="text-xs text-muted-foreground uppercase">Unbilled Expenses</p>
                          <div className="space-y-1">
                            <div className="flex justify-between"><span className="text-xs font-medium">Current:</span><span className={`text-xs ${(horseMetrics.unbilled_expenses ?? 0) > 0 ? 'text-amber-600 font-semibold' : ''}`}>{fmtCurrency(horseMetrics.unbilled_expenses ?? 0)}</span></div>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </CardContent>
            </Card>
            {/* Default Billing Entity — only shown when a secondary entity exists */}
            {billingEntities.length > 0 && horse && (
              <Card className="mb-4">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Default Billing Entity</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 pb-4">
                  <p className="text-xs text-muted-foreground mb-2">
                    When an invoice is created for this horse, it will automatically be assigned to the selected billing entity.
                  </p>
                  <div className="flex items-center gap-3 max-w-xs">
                    <Select
                      value={horse.default_billing_entity_id || 'primary'}
                      onValueChange={async (val) => {
                        const entityId = val === 'primary' ? null : val;
                        setSavingBillingEntity(true);
                        try {
                          await api.put(`/horses/${horse.id}`, { default_billing_entity_id: entityId });
                          qc.setQueryData(QK.horse(id), prev => prev ? { ...prev, horse: { ...prev.horse, default_billing_entity_id: entityId } } : prev);
                        } catch {
                          toast.error('Failed to update billing entity');
                        }
                        setSavingBillingEntity(false);
                      }}
                    >
                      <SelectTrigger disabled={savingBillingEntity}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="primary">{tenantName || 'Primary Organization'}</SelectItem>
                        {billingEntities.map(be => (
                          <SelectItem key={be.id} value={be.id}>{be.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {savingBillingEntity && <span className="text-xs text-muted-foreground">Saving…</span>}
                  </div>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Invoices</CardTitle>
                </div>
                <div className="relative w-56 mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input className="pl-9 h-8 text-sm" placeholder="Search invoices..." value={horseInvoiceSearch} onChange={(e) => setHorseInvoiceSearch(e.target.value)} data-testid="horse-invoice-search" />
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6 pt-0">
                {isLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : (() => {
                  const q = horseInvoiceSearch.toLowerCase();
                  const filtered = q ? horseInvoices.filter(inv => {
                    const beName = inv.billing_entity_id
                      ? (billingEntities.find(e => e.id === inv.billing_entity_id)?.name || '')
                      : (tenantName || 'primary');
                    return (
                      (inv.invoice_number_formatted || '').toLowerCase().includes(q) ||
                      (inv.crm_entity_name || '').toLowerCase().includes(q) ||
                      (inv.invoice_status || '').toLowerCase().includes(q) ||
                      (inv.payment_status || '').toLowerCase().includes(q) ||
                      (inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '').includes(q) ||
                      beName.toLowerCase().includes(q)
                    );
                  }) : horseInvoices;
                  return filtered.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/20" />
                    <p className="text-sm text-muted-foreground">{horseInvoiceSearch ? 'No matching invoices' : 'No approved invoices for this horse'}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {billingEntities.length > 0 && <TableHead>Billing Entity</TableHead>}
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Account / Owner</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map(inv => {
                        const entries = inv.entries || [];
                        const expTotal = entries.filter(e => !e.sale_opportunity_id).reduce((s, e) => s + (e.total || e.price * e.units), 0) - (inv.discount_total || 0);
                        const saleProceeds = inv.add_sale_proceeds ? ((inv.sale_net_proceeds || 0) - (inv.commission_amount || 0)) : 0;
                        const total = inv.add_sale_proceeds ? (saleProceeds - expTotal) : expTotal;
                        const amount = ((inv.owner_percent || 100) / 100) * total;
                        const payable = (inv.add_sale_proceeds && saleProceeds > expTotal) || inv.is_commission_split_invoice;
                        const fmtAmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);
                        const beName = inv.billing_entity_id
                          ? (billingEntities.find(e => e.id === inv.billing_entity_id)?.name || '–')
                          : (tenantName || '–');
                        return (
                          <TableRow key={inv.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setPreviewInvoiceId(inv.id); setPreviewOpen(true); }} data-testid={`horse-invoice-row-${inv.id}`}>
                            {billingEntities.length > 0 && <TableCell>{beName}</TableCell>}
                            <TableCell className="font-medium">{inv.invoice_number_formatted || `#${inv.invoice_number}`}</TableCell>
                            <TableCell>{inv.crm_entity_name || '-'}</TableCell>
                            <TableCell className="text-sm">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</TableCell>
                            <TableCell><Badge className="bg-blue-100 text-blue-700">{inv.invoice_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                            <TableCell><Badge className={inv.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{inv.payment_status?.replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                            <TableCell className={`text-right font-medium ${payable ? 'text-primary' : ''}`}>
                              {payable ? `(${fmtAmt(amount)})` : fmtAmt(amount)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                );
                })()}
              </CardContent>
            </Card>
            <InvoicePdfPreviewDialog
              open={previewOpen}
              onOpenChange={setPreviewOpen}
              type="invoice"
              invoiceId={previewInvoiceId}
            />
          </TabsContent>

          <TabsContent value="sales-opportunities">
            <Card>
              <CardHeader>
                <CardTitle>Sale Opportunities</CardTitle>
                <div className="relative max-w-xs mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search opportunities..." value={oppsSearch} onChange={(e) => setOppsSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                </div>
              </CardHeader>
              <CardContent className="px-4 py-0 pb-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sale</TableHead>
                      <TableHead>Presale Stage</TableHead>
                      <TableHead>Hip #</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Sale Price</TableHead>
                      <TableHead>Date Created</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const q = oppsSearch.toLowerCase();
                      const filtered = saleOpportunities.filter(opp => {
                        if (!q) return true;
                        const sale = sales[opp.sale_id];
                        return [sale?.sale_name, sale?.sale_company, opp.opportunity_name, opp.hip_number?.toString(), opp.result, opp.sale_price?.toString()].some(v => (v || '').toLowerCase().includes(q));
                      });
                      return filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <Gavel className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                          <p className="text-muted-foreground">No sale opportunities for this horse</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((opp) => {
                        const sale = sales[opp.sale_id];
                        const stageBadge = stageBadges[opp.stage];
                        const resultBadge = resultBadges[opp.result];

                        return (
                          <TableRow
                            key={opp.id}
                            className={isFarmMgr ? '' : 'cursor-pointer hover:bg-muted/50'}
                            onClick={isFarmMgr ? undefined : () => navigate(`/sales/opportunities/${opp.id}`)}
                          >
                            <TableCell className="text-sm">
                              <div>
                                <p className="font-medium">
                                  {sale?.sale_name || opp.opportunity_name?.split(' - ')[0] || '-'}
                                </p>
                                {sale && (
                                  <p className="text-xs text-muted-foreground">
                                    {sale.sale_company} • {new Date(sale.start_date || sale.sale_start_date).getFullYear()}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {stageBadge ? (
                                <Badge className={stageBadge.className}>{stageBadge.label}</Badge>
                              ) : (
                                <span>-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">#{opp.hip_number || 'TBD'}</TableCell>
                            <TableCell className="text-sm">
                              {resultBadge ? (
                                <Badge className={resultBadge.className}>{resultBadge.label}</Badge>
                              ) : (
                                <span>-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm font-medium">
                              {opp.sale_price ? `$${opp.sale_price.toLocaleString()}` : '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {opp.created_at ? new Date(opp.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {!isFarmMgr && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    );
                    })()}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="farmfolio">
            <FarmFolioTab horse={horse} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Profile Photo Upload Dialog */}
      <Dialog open={profilePhotoPreview} onOpenChange={setProfilePhotoPreview}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Horse Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-2 flex-1 overflow-hidden">
            {(profilePhotoDataUrl || horse?.main_photo_url) ? (
              <div className="flex items-center justify-center bg-muted/30 rounded-lg p-2 overflow-hidden">
                <img
                  src={profilePhotoDataUrl || (horse.main_photo_url.startsWith('http') ? horse.main_photo_url : `${window.location.origin}${horse.main_photo_url}`)}
                  alt={displayName}
                  className="max-w-full max-h-[280px] object-contain rounded-lg"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="h-48 bg-muted rounded-lg flex items-center justify-center">
                <HorseshoeIcon className="w-24 h-24 text-muted-foreground" />
              </div>
            )}
          </div>
          {!viewOnly && (
          <div className="px-6 pb-4 pt-2 flex flex-wrap gap-2 shrink-0">
            {uploadingPhoto ? (
              <Button disabled className="w-full">Uploading...</Button>
            ) : (
              <>
                {(profilePhotoDataUrl || horse?.main_photo_url) && (
                  <Button variant="outline" size="sm" onClick={handleRotateAndSave} disabled={savingRotation}>
                    {savingRotation ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
                    Rotate
                  </Button>
                )}
                <input type="file" id="horse-photo-upload" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => document.getElementById('horse-photo-upload').click()}>
                  <Upload className="w-4 h-4 mr-1" /> Upload
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCameraOpen(true)} data-testid="horse-take-photo-btn">
                  <Camera className="w-4 h-4 mr-1" /> Camera
                </Button>
              </>
            )}
          </div>
          )}
        </DialogContent>
      </Dialog>
      <CameraCaptureDialog
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={handleCameraCapture}
      />
      <CreateSaleOpportunityDialog
        open={showCreateOppDialog}
        onClose={() => { setShowCreateOppDialog(false); setCreateOppSavesStatus(false); }}
        horse={horse}
        onSuccess={createOppSavesStatus ? handleCreateOppSuccess : null}
      />
    </SidebarLayout>
  );
};

export default HorseDetailPage;
